import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from 'config/firebase';
import CardWithTitle from 'components/CardWithTitle';
import Loader from 'components/common/Loader';
import { FSBTable } from 'components/common/FSBTable';
import { useAssignedVendors } from 'hooks/useAssignedVendors';
import { FaChevronDown } from 'react-icons/fa6';
import './components/AllTabsAndTables/dashboard-mobile.css';

const normalizeText = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (text === '' || text === '-' || text.toLowerCase() === 'n/a') return '';
  return text;
};

const formatRate = (rate) => {
  const normalized = normalizeText(rate);
  if (!normalized) return '';
  const numericRate = Number(normalized);
  if (Number.isNaN(numericRate)) return '';
  return numericRate.toFixed(2);
};

const formatDateTime = (date) => {
  if (!date) return 'N/A';

  const time = date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const dateStr = date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return `${time} ${dateStr}`;
};

const getTypeDisplay = (type) => {
  if (!type) return 'N/A';
  const normalized = String(type).trim().toLowerCase();
  const typeMap = {
    offer_view: 'View Offers',
    demand_view: 'View Demands',
    offer_create: 'Create Offer',
    demand_create: 'Create Demand',
    offer: 'Offer Details',
    demand: 'Demand Details',
  };
  if (typeMap[normalized]) return typeMap[normalized];
  return normalized
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getTypeFilterKey = (type) =>
  getTypeDisplay(type).trim().toLowerCase().replace(/\s+/g, ' ');

const formatRateDisplay = (item) => {
  const rateStr = formatRate(item.rate);
  if (!rateStr) return item.location || '';
  if (item.location) return `${item.location}, \u20b9 ${rateStr}`;
  return `\u20b9 ${rateStr}`;
};

const SerialNumberCell = ({ row, table }) =>
  table.options.meta.startIndex + row.index + 1;

const UserCountCell = ({ row, table }) => {
  const item = row.original;
  const { newEntries } = table.options.meta;

  return (
    <div className="d-flex align-items-center">
      {item.userName ? (
        <span className="fw-medium">{item.userName}</span>
      ) : null}
      <span
        className={`text-primary fw-semibold ${item.userName ? 'ms-2' : ''}`}
      >
        ({item.count})
      </span>
      {newEntries.has(`${item.gradeName}-${item.userId}`) && (
        <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>
          NEW
        </span>
      )}
    </div>
  );
};

const GradeNameCell = ({ row }) => (
  <div className="fw-medium">
    <div>{row.original.gradeName}</div>
    {(row.original.location || formatRate(row.original.rate)) && (
      <div className="f-12 text-muted">
        {formatRateDisplay(row.original)}
      </div>
    )}
  </div>
);

const LastOpenedCell = ({ getValue }) => {
  const lastOpened = getValue();
  if (lastOpened) return formatDateTime(lastOpened);
  return <span className="text-muted">N/A</span>;
};

const TypeHeader = ({ table }) => {
  const { onTypeChange, selectedType, typeOptions } = table.options.meta;

  return (
    <div className="d-flex align-items-center justify-content-between gap-2">
      <span>TYPE</span>
      <select
        id="history-type-filter"
        className="form-select form-select-sm type-filter-select"
        value={selectedType}
        onChange={onTypeChange}
        aria-label="Filter by type"
      >
        <option value="all">All</option>
        {typeOptions.map(({ key, label }) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
};

const TypeCell = ({ getValue }) => getTypeDisplay(getValue());

const FirebaseGradeTouchData = () => {
  const [gradeData, setGradeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedType, setSelectedType] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [newEntries, setNewEntries] = useState(new Set());
  const [expandedCardId, setExpandedCardId] = useState(null);
  const previousDataRef = useRef([]);

  // Get assigned vendors for logged-in user
  const {
    assignedVendorIds,
    loading: vendorsLoading,
    isSalesRole,
    hasAssignedVendors,
  } = useAssignedVendors();

  // ✅ Wrapped in useCallback to satisfy react-hooks/exhaustive-deps
  const fetchGradeTouchData = useCallback(
    async (isRefresh = false) => {
      if (vendorsLoading) return;

      try {
        if (!isRefresh) {
          setLoading(true);
        }
        setError(null);

        const gradeDocs = await getDocs(collection(db, 'offers_views'));
        const gradesArray = [];
        const legacyRef = doc(db, 'offers_views', 'global_counts');
        const legacyDoc = await getDoc(legacyRef);
        const gradeResults = await Promise.all(
          gradeDocs.docs
            .filter((gradeDoc) => gradeDoc.id !== 'global_counts')
            .map(async (gradeDoc) => {
              const usersSnapshot = await getDocs(
                collection(db, 'offers_views', gradeDoc.id, 'users'),
              );
              return {
                gradeName: gradeDoc.data().gradeName || gradeDoc.id,
                users: usersSnapshot.docs.map((userDoc) => ({
                  userId: userDoc.id,
                  userData: userDoc.data(),
                })),
              };
            }),
        );

        gradeResults.forEach(({ gradeName, users }) => {
          users.forEach(({ userId, userData }) => {
              let shouldInclude = false;

              if (assignedVendorIds === null) {
                shouldInclude = true;
              } else if (
                assignedVendorIds.phones &&
                assignedVendorIds.phones.length > 0
              ) {
                const userPhone = userData.number?.replace(/\D/g, '');
                shouldInclude = assignedVendorIds.phones.some(
                  (phone) => phone?.replace(/\D/g, '') === userPhone,
                );
              }

              if (shouldInclude) {
                const userName = normalizeText(userData.name);
                const userNumber = normalizeText(userData.number);
                if (!userName && !userNumber) {
                  return;
                }
                gradesArray.push({
                  gradeName,
                  userId,
                  userName,
                  userNumber,
                  count: userData.count || 0,
                  lastClicked: userData.lastClicked?.toDate() || null,
                  type: userData.type || 'N/A',
                  location: normalizeText(userData.location),
                  rate: normalizeText(userData.rate),
                });
              }
          });
        });

        // Keep rows from older clients visible while they still exist in the legacy document.
        if (legacyDoc.exists()) {
          Object.entries(legacyDoc.data()).forEach(([gradeName, gradeDataItem]) => {
            if (!gradeDataItem?.users) return;
            Object.entries(gradeDataItem.users).forEach(([userId, userData]) => {
              const alreadyIncluded = gradesArray.some(
                (item) => item.gradeName === gradeName && item.userId === userId,
              );
              if (alreadyIncluded) return;
              const userName = normalizeText(userData.name);
              const userNumber = normalizeText(userData.number);
              if (!userName && !userNumber) return;
              const phone = userNumber.replace(/\D/g, '');
              const shouldInclude = assignedVendorIds === null ||
                (assignedVendorIds.phones || []).some((assignedPhone) =>
                  assignedPhone?.replace(/\D/g, '') === phone,
                );
              if (!shouldInclude) return;
              gradesArray.push({
                gradeName,
                userId,
                userName,
                userNumber,
                count: userData.count || 0,
                lastClicked: userData.lastClicked?.toDate() || null,
                type: userData.type || 'N/A',
                location: normalizeText(userData.location),
                rate: normalizeText(userData.rate),
              });
            });
          });
        }

        gradesArray.sort((a, b) => {
          if (!a.lastClicked && !b.lastClicked) return 0;
          if (!a.lastClicked) return 1;
          if (!b.lastClicked) return -1;
          return b.lastClicked - a.lastClicked;
        });

        if (isRefresh && previousDataRef.current.length > 0) {
          const previousIds = new Set(
            previousDataRef.current.map(
              (item) => `${item.gradeName}-${item.userId}`,
            ),
          );
          const newIds = new Set();

          gradesArray.forEach((item) => {
            const id = `${item.gradeName}-${item.userId}`;
            if (!previousIds.has(id)) {
              newIds.add(id);
            }
          });

          setNewEntries(newIds);
        }

        previousDataRef.current = gradesArray;
        setGradeData(gradesArray);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    },
    [assignedVendorIds, vendorsLoading],
  );

  useEffect(() => {
    if (!vendorsLoading) {
      fetchGradeTouchData(false);
    }
  }, [vendorsLoading, fetchGradeTouchData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setNewEntries(new Set());
    await fetchGradeTouchData(true);
    setIsRefreshing(false);
  };

  const typeOptions = useMemo(() => {
    const baseOrder = [
      'offer_view',
      'demand_view',
      'offer_create',
      'demand_create',
      'offer',
      'demand',
    ];
    const uniqueTypes = Array.from(
      new Set(gradeData.map((item) => item.type).filter(Boolean)),
    );
    const ordered = baseOrder.filter((type) => uniqueTypes.includes(type));
    const remaining = uniqueTypes
      .filter((type) => !baseOrder.includes(type))
      .sort((a, b) => getTypeDisplay(a).localeCompare(getTypeDisplay(b)));
    const seenLabels = new Set();
    return [...ordered, ...remaining].reduce((options, type) => {
      const key = getTypeFilterKey(type);
      if (!key || key.includes('search') || seenLabels.has(key)) return options;

      seenLabels.add(key);
      options.push({ key, label: getTypeDisplay(type) });
      return options;
    }, []);
  }, [gradeData]);

  const filteredData =
    selectedType === 'all'
      ? gradeData
      : gradeData.filter((item) => getTypeFilterKey(item.type) === selectedType);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = ({ pageNo, pageSize: nextPageSize }) => {
    setCurrentPage(pageNo);
    setPageSize(nextPageSize);
  };

  const getCardTitle = () => {
    if (isSalesRole && hasAssignedVendors) {
      return 'History (Assigned Vendors Only)';
    }
    return 'History';
  };

  const getEmptyMessage = () => {
    if (isSalesRole) {
      return 'No grade touch data available for your assigned vendors';
    }
    return 'No grade touch data available';
  };

  const isNewEntry = (item) =>
    newEntries.has(`${item.gradeName}-${item.userId}`);

  const handleTypeChange = useCallback((event) => {
    setSelectedType(event.target.value);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const columns = useMemo(
    () => [
      {
        id: 'serialNumber',
        header: 'S.NO',
        size: 62,
        minSize: 62,
        enableSorting: false,
        cell: SerialNumberCell,
      },
      {
        accessorKey: 'userName',
        header: 'USER (COUNT)',
        size: 220,
        minSize: 180,
        enableSorting: false,
        cell: UserCountCell,
      },
      {
        accessorKey: 'userNumber',
        header: 'NUMBER',
        size: 150,
        minSize: 130,
        enableSorting: false,
      },
      {
        accessorKey: 'gradeName',
        header: 'GRADE NAME',
        size: 190,
        minSize: 170,
        enableSorting: false,
        cell: GradeNameCell,
      },
      {
        accessorKey: 'lastClicked',
        header: 'LAST OPENED',
        size: 190,
        minSize: 170,
        enableSorting: false,
        cell: LastOpenedCell,
      },
      {
        accessorKey: 'type',
        header: TypeHeader,
        size: 210,
        minSize: 190,
        enableSorting: false,
        cell: TypeCell,
      },
    ],
    [],
  );

  const tableMeta = useMemo(
    () => ({
      startIndex,
      newEntries,
      selectedType,
      typeOptions,
      onTypeChange: handleTypeChange,
    }),
    [handleTypeChange, newEntries, selectedType, startIndex, typeOptions],
  );

  const renderCustomTitle = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <span id="history-title">{getCardTitle()}</span>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={isRefreshing || loading}
        className="btn btn-sm btn-link p-0 ms-2"
        style={{
          border: 'none',
          background: 'transparent',
          cursor: isRefreshing ? 'not-allowed' : 'pointer',
          opacity: isRefreshing ? 0.6 : 1,
        }}
        title="Refresh data"
        aria-label="Refresh data"
        aria-describedby="history-title"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
          }}
          aria-hidden="true"
        >
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
        </svg>
      </button>
    </div>
  );

  if (loading || vendorsLoading) {
    return (
      <CardWithTitle title={renderCustomTitle()}>
        <Loader />
      </CardWithTitle>
    );
  }

  if (error) {
    return (
      <CardWithTitle title={renderCustomTitle()}>
        <div className="text-danger f-12">
          Error loading grade touch data: {error}
        </div>
        <div className="text-muted f-11 mt-2">Check console for details</div>
      </CardWithTitle>
    );
  }

  if (gradeData.length === 0) {
    return (
      <CardWithTitle title={renderCustomTitle()}>
        <div className="mb-2 text-active f-12">{getEmptyMessage()}</div>
      </CardWithTitle>
    );
  }

  if (filteredData.length === 0) {
    return (
      <CardWithTitle title={renderCustomTitle()}>
        <div className="mb-2 text-active f-12">
          No records found for the selected type
        </div>
      </CardWithTitle>
    );
  }

  return (
    <CardWithTitle title={renderCustomTitle()}>
      <style>
        {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to   { transform: rotate(360deg); }
                    }

                    @keyframes highlight-fade {
                        0%   { background-color: #d4edda; }
                        100% { background-color: #f0f9f4; }
                    }

                    .new-entry-row {
                        background-color: #f0f9f4;
                        animation: highlight-fade 0.5s ease-in-out;
                        border-left: 3px solid #28a745 !important;
                    }

                    .new-entry-row:hover {
                        background-color: #e2f4e8 !important;
                    }

                    .new-entry-row:hover td {
                        background-color: #e2f4e8 !important;
                    }

                    .table-hover tbody tr:hover {
                        background-color: #f8f9fa;
                    }

                    .new-entry-row td {
                        position: relative;
                        background-color: #f0f9f4 !important;
                    }

                    .type-filter-select {
                        width: 140px;
                        height: 28px;
                        padding: 2px 8px;
                        font-size: 12px;
                        font-weight: 600;
                        font-family: inherit;
                        text-transform: none;
                        color: #495057;
                    }
                `}
      </style>

      <div className="dashboard-mobile-list">
        {currentData.map((item, index) => {
          const uniqueKey = `${item.gradeName}-${item.userId}-${item.lastClicked?.getTime() || index}`;
          const isExpanded = expandedCardId === uniqueKey;

          return (
            <div
              className="dashboard-mobile-card"
              key={uniqueKey}
              style={{
                borderLeft: isNewEntry(item) ? '3px solid #28a745' : '',
              }}
            >
              <div className="dashboard-mobile-card__summary">
                <div className="dashboard-mobile-card__identity">
                  <span className="dashboard-mobile-card__name">
                    {item.userName ? item.userName : 'Unknown'} ({item.count})
                    {isNewEntry(item) && (
                      <span
                        className="badge bg-success ms-2"
                        style={{ fontSize: '0.65rem' }}
                      >
                        NEW
                      </span>
                    )}
                  </span>
                  <div className="dashboard-mobile-card__meta">
                    {item.userNumber || 'No number'} | {item.gradeName}
                  </div>
                </div>
                <div className="dashboard-mobile-card__aside">
                  <span className="dashboard-mobile-card__pill muted">
                    {getTypeDisplay(item.type)}
                  </span>
                  <div className="dashboard-mobile-card__tools">
                    <button
                      type="button"
                      className={`dashboard-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''}`}
                      onClick={() => {
                        setExpandedCardId((prev) => (
                          prev === uniqueKey ? null : uniqueKey
                        ));
                      }}
                    >
                      <FaChevronDown />
                    </button>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="dashboard-mobile-card__body">
                  <div className="dashboard-mobile-card__detail-list">
                    <div className="dashboard-mobile-card__detail-row">
                      <div className="dashboard-mobile-card__detail-label">
                        Last Opened
                      </div>
                      <div className="dashboard-mobile-card__detail-value">
                        {item.lastClicked
                          ? formatDateTime(item.lastClicked)
                          : 'N/A'}
                      </div>
                    </div>
                    {(item.location || formatRate(item.rate)) && (
                      <div className="dashboard-mobile-card__detail-row">
                        <div className="dashboard-mobile-card__detail-label">
                          Rate/Location
                        </div>
                        <div className="dashboard-mobile-card__detail-value">
                          {formatRateDisplay(item)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="dashboard-history-table">
        <FSBTable
          data={currentData}
          columns={columns}
          meta={tableMeta}
          totalCount={filteredData.length}
          totalPages={totalPages}
          currentPageNo={currentPage}
          pageSize={pageSize}
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination
          handlePageChange={handlePageChange}
          tableWraperClass="dashboard-mobile-hide-table"
          tableClass="table-hover"
          getRowClassName={(item) => (isNewEntry(item) ? 'new-entry-row' : '')}
        />
      </div>
    </CardWithTitle>
  );
};

export default FirebaseGradeTouchData;
